import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Fingerprint, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { setVaultToken, biometricSupported, unlockWithBiometric } from "@/lib/vault";

export function VaultToggle({ on, onChange, hint }) {
  return (
    <label className="ga-card mt-4 flex w-full cursor-pointer items-center gap-3 p-3 text-left" data-testid="vault-save-toggle">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl" style={{ background: "#05324A", color: "#33D9B2" }}><Lock size={18} /></div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">Salvar na Pasta Segura</div>
        <div className="ga-muted text-xs">{hint || "Protegido por PIN. Fica fora da busca e do “Pergunte”."}</div>
      </div>
      <Switch checked={on} onCheckedChange={onChange} data-testid="vault-save-switch" />
    </label>
  );
}

export function ChangePinForm({ onDone }) {
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [cf, setCf] = useState(""); const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    if (nw !== cf) return toast.error("Os PINs não coincidem.");
    setBusy(true);
    try { await api.post("/vault/change-pin", { current_pin: cur, new_pin: nw }); toast.success("PIN alterado."); onDone?.(); } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={go} className="space-y-2" data-testid="vault-change-pin-form">
      <input type="password" inputMode="numeric" autoComplete="off" placeholder="PIN atual" value={cur} onChange={(e) => setCur(e.target.value)} className="ga-input py-2" data-testid="vault-change-pin-current" />
      <input type="password" inputMode="numeric" autoComplete="off" placeholder="Novo PIN (4 a 8 dígitos)" value={nw} onChange={(e) => setNw(e.target.value)} maxLength={32} className="ga-input py-2" data-testid="vault-change-pin-new" />
      <input type="password" inputMode="numeric" autoComplete="off" placeholder="Confirme o novo PIN" value={cf} onChange={(e) => setCf(e.target.value)} maxLength={32} className="ga-input py-2" data-testid="vault-change-pin-confirm" />
      <button type="submit" disabled={busy || cur.length < 4 || nw.length < 4} className="ga-btn-primary w-full py-2.5" data-testid="vault-change-pin-save">{busy ? "Aguarde..." : "Salvar novo PIN"}</button>
    </form>
  );
}

export function PinRecovery({ info, onDone, onCancel }) {
  const [step, setStep] = useState("intro");
  const [code, setCode] = useState("");
  const [token, setToken] = useState(null);
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const noEmail = !info?.masked_email;
  const [unavailable, setUnavailable] = useState(noEmail ? "Sua conta não tem e-mail. Adicione um e-mail em Perfil → Segurança para habilitar a recuperação do PIN. Enquanto isso, o acesso é somente pelo PIN atual." : null);

  const sendCode = async () => {
    setBusy(true);
    try { await api.post("/vault/recovery/request"); toast.success(`Código enviado para ${info.masked_email}.`); setCode(""); setStep("code"); }
    catch (e) { if ([400, 503, 409].includes(e?.response?.status)) setUnavailable(errorMessage(e)); else toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/vault/recovery/verify", { code }); setToken(data.reset_token); setStep("newpin"); }
    catch (err) { toast.error(errorMessage(err)); if (err?.response?.status === 429) { setStep("intro"); setCode(""); } }
    finally { setBusy(false); }
  };
  const reset = async (e) => {
    e.preventDefault();
    if (pin !== confirm) return toast.error("Os PINs não coincidem.");
    setBusy(true);
    try { const { data } = await api.post("/vault/recovery/reset", { reset_token: token, new_pin: pin }); toast.success("Novo PIN criado. Todo o conteúdo da pasta foi preservado."); onDone(data); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };
  const inputCls = "ga-input mt-1 text-center text-2xl tracking-[0.5em]";

  return (
    <div className="ga-fade-up px-5" data-testid="vault-recovery">
      <div className="flex flex-col items-center py-8 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-3xl" style={{ background: "#05324A" }}><Lock size={36} color="#33D9B2" /></div>
        <h2 className="mt-5 text-xl font-semibold">{step === "newpin" ? "Criar novo PIN" : "Recuperar PIN"}</h2>
        <p className="ga-muted mt-1 max-w-xs text-sm">
          {step === "intro" && (noEmail ? "A recuperação do PIN por e-mail não está disponível para contas sem e-mail." : <>Enviaremos um código temporário de 4 dígitos para <span className="font-semibold" data-testid="vault-recovery-email">{info.masked_email}</span>.</>)}
          {step === "code" && "Digite o código que enviamos por e-mail. Ele expira em poucos minutos."}
          {step === "newpin" && "Defina um novo PIN de 4 a 8 dígitos. Nada do que está guardado será alterado."}
        </p>
      </div>
      {unavailable && <div className="ga-card mb-3 p-3 text-sm" data-testid="vault-recovery-unavailable">{unavailable}</div>}
      {step === "intro" && <button onClick={sendCode} disabled={busy || !!unavailable} className="ga-btn-primary w-full" data-testid="vault-recovery-send-button">{busy ? "Enviando..." : "Enviar código"}</button>}
      {step === "code" && (
        <form onSubmit={verify} className="space-y-3">
          <label className="block text-xs font-medium ga-muted">Código<input type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))} className={inputCls} data-testid="vault-recovery-code-input" /></label>
          <button type="submit" disabled={busy || code.length !== 4} className="ga-btn-primary w-full" data-testid="vault-recovery-verify-button">{busy ? "Verificando..." : "Validar código"}</button>
          <button type="button" onClick={sendCode} disabled={busy} className="ga-muted w-full text-center text-xs underline" data-testid="vault-recovery-resend-button">Reenviar código</button>
        </form>
      )}
      {step === "newpin" && (
        <form onSubmit={reset} className="space-y-3">
          <label className="block text-xs font-medium ga-muted">Novo PIN<input type="password" inputMode="numeric" autoComplete="off" autoFocus value={pin} onChange={(e) => setPin(e.target.value)} maxLength={32} className={inputCls} data-testid="vault-recovery-pin-input" /></label>
          <label className="block text-xs font-medium ga-muted">Confirme o novo PIN<input type="password" inputMode="numeric" autoComplete="off" value={confirm} onChange={(e) => setConfirm(e.target.value)} maxLength={32} className={inputCls} data-testid="vault-recovery-pin-confirm-input" /></label>
          <button type="submit" disabled={busy || pin.length < 4} className="ga-btn-primary w-full" data-testid="vault-recovery-save-button">{busy ? "Salvando..." : "Salvar novo PIN"}</button>
        </form>
      )}
      <button type="button" onClick={onCancel} className="ga-muted mt-4 w-full text-center text-xs underline" data-testid="vault-recovery-cancel">Voltar</button>
    </div>
  );
}

export function VaultPinDialog({ open, onUnlocked, onClose }) {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [bio, setBio] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPin(""); setInfo(null);
    api.get("/vault").then((r) => setInfo(r.data)).catch((e) => { toast.error(errorMessage(e)); onClose(); });
    biometricSupported().then(setBio);
  }, [open, onClose]);

  const unlock = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/vault/unlock", { pin });
      setVaultToken(data.vault_token);
      onUnlocked();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const useBio = async () => {
    try { const data = await unlockWithBiometric(); setVaultToken(data.vault_token); onUnlocked(); } catch (err) { toast.error(err?.response ? errorMessage(err) : "Biometria não reconhecida. Use o PIN."); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm rounded-3xl" data-testid="vault-pin-dialog">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl" style={{ background: "#05324A" }}>{info?.exists === false ? <ShieldCheck size={22} color="#33D9B2" /> : <Lock size={22} color="#33D9B2" />}</div>
          <div>
            <DialogTitle className="text-lg">{info?.exists === false ? "Crie sua Pasta Segura" : "Pasta Segura bloqueada"}</DialogTitle>
            <DialogDescription className="text-sm">{info?.exists === false ? "Você ainda não tem uma Pasta Segura. Crie uma para guardar este conteúdo." : "Digite seu PIN para salvar este item na Pasta Segura."}</DialogDescription>
          </div>
        </div>
        {info?.exists === false ? (
          <button onClick={() => navigate("/pasta-segura")} className="ga-btn-primary w-full" data-testid="vault-pin-create-link">Criar Pasta Segura</button>
        ) : (
          <form onSubmit={unlock} className="space-y-3">
            <input type="password" inputMode="numeric" autoComplete="off" autoFocus value={pin} onChange={(e) => setPin(e.target.value)} maxLength={32} className="ga-input text-center text-2xl tracking-[0.5em]" data-testid="vault-pin-dialog-input" />
            <button type="submit" disabled={busy || pin.length < 4 || !info} className="ga-btn-primary w-full" data-testid="vault-pin-dialog-submit">{busy ? "Aguarde..." : "Desbloquear e salvar"}</button>
            {info?.biometric_enabled && bio && <button type="button" onClick={useBio} className="ga-btn-ghost w-full" data-testid="vault-pin-dialog-biometric"><Fingerprint size={18} /> Usar biometria</button>}
            <button type="button" onClick={() => navigate("/pasta-segura", { state: { recover: true } })} className="ga-muted w-full text-center text-xs underline" data-testid="vault-pin-dialog-forgot">Esqueci meu PIN</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
